import DateUtils from "../../../shared/utils/DateUtils";
import MonetaryUtils from "../../../shared/utils/MonetaryUtils";

export const getPercentageSelectValue = value => {
	if (value === undefined || value === null || value === "") {
		return "";
	}

	return String(MonetaryUtils.parseDecimal(value));
};

export const buildEquipmentOptions = clientContracts => {
	return (clientContracts || []).flatMap(contract => {
		return (contract.equipmentList || []).map(equipment => ({
			...equipment,
			value: `${contract.contractId}-${equipment.invoiceNumber}`,
			label: equipment.label,
			contractId: contract.contractId,
			openCycle: contract.openCycle,
			openCycleValue: contract.openCycleValue,
			openCycleApiValue: contract.openCycleApiValue
		}));
	});
};

export const buildCycleOptions = contractCycles => {
	return (contractCycles || []).map(cycle => ({
		label: cycle.label,
		value: cycle.value
	}));
};

export const buildPurchaseSummary = ({
	dashboardCycle,
	selectedContract
}) => {
	if (dashboardCycle?.purchaseSummary) {
		return {
			totalValue: dashboardCycle.purchaseSummary.totalValue,
			purchaseDate: dashboardCycle.purchaseSummary.firstInstallmentDate || "-",
			downPaymentValue: dashboardCycle.purchaseSummary.downPaymentValue,
			installmentsQuantity: dashboardCycle.purchaseSummary.installmentsQuantity,
			installments: dashboardCycle.purchaseSummary.installments
		};
	}

	return {
		totalValue: selectedContract?.totalValue ?? 0,
		purchaseDate: selectedContract?.formattedPurchaseDate || "-",
		downPaymentValue: selectedContract?.downPaymentValue ?? 0,
		installmentsQuantity: selectedContract?.installmentsQuantity ?? "-",
		installments: selectedContract?.installments || []
	};
};

export const buildMonthlySalesChartData = dashboardCycle => {
	if (!dashboardCycle?.sales?.monthly) {
		return [];
	}

	return [
		...dashboardCycle.sales.monthly.historicalSeries,
		...dashboardCycle.sales.monthly.projectedSeries
	];
};

export const buildDailyAverageChartData = ({
	dashboardCycle,
	isClosedCycle
}) => {
	if (!dashboardCycle?.sales?.daily) {
		return [];
	}

	const dailySales = dashboardCycle.sales.daily;
	const currentDayTimestamp = DateUtils.parseDateOnlyToTimestamp(
		dailySales.currentDay
	);

	const shouldShowCurrentDayPoint =
		!isClosedCycle && currentDayTimestamp;

	return [
		...dailySales.historicalSeries,
		...dailySales.projectedSeries,
		...(shouldShowCurrentDayPoint
			? [{
				date: currentDayTimestamp,
				month: DateUtils.formatDateToDayAndMonth(currentDayTimestamp),
				dailyAverage: null,
				currentDailySales: dailySales.dailyAverage,
				type: "current"
			}]
			: [])
	];
};

export const buildVariluxMonthlyChartData = dashboardCycle => {
	if (!dashboardCycle?.varilux) {
		return [];
	}

	return [
		...dashboardCycle.varilux.closedSeries,
		...dashboardCycle.varilux.projectedSeries
	];
};

export const buildDailyAverageTargetOptions = dailyGrowthSimulation => {
	const ranges = dailyGrowthSimulation?.ranges || [];

	return ranges.map(range => ({
		label: MonetaryUtils.formatPercentage(range.discountPercentage),
		value: getPercentageSelectValue(range.discountPercentage)
	}));
};

export const findDailyGrowthRangeByDiscount = ({
	dailyGrowthSimulation,
	dailyAverageTarget
}) => {
	const ranges = dailyGrowthSimulation?.ranges || [];

	return ranges.find(range => {
		return (
			getPercentageSelectValue(range.discountPercentage) ===
			dailyAverageTarget
		);
	});
};

export const shouldShowGoalsSideCard = ({
	goals = [],
	baseline = 0,
	data = [],
	dataKeys = [],
	minDistanceRatio = 0.1
}) => {
	if (!goals.length) {
		return false;
	}

	const goalValues = goals.map(goal => Number(goal.minimumValue || 0));
	const dataValues = data.flatMap(item => {
		return dataKeys.map(dataKey => Number(item?.[dataKey] || 0));
	});

	const chartMaxValue = Math.max(
		Number(baseline || 0),
		...goalValues,
		...dataValues,
		0
	);

	if (!chartMaxValue) {
		return false;
	}

	const sortedValues = [...goalValues, Number(baseline || 0)]
		.filter(value => value > 0)
		.sort((firstValue, secondValue) => firstValue - secondValue);

	return sortedValues.some((value, index) => {
		if (index === 0) {
			return false;
		}

		const previousValue = sortedValues[index - 1];
		const distanceRatio = Math.abs(value - previousValue) / chartMaxValue;

		return distanceRatio < minDistanceRatio;
	});
};